import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import filter from 'lodash/filter';

import {
  Box,
  Typography,
  CircularProgress,
  CardContent,
  Button
} from '@mui/material';

import {
  installFailedToTrack,
  installToTrack,
} from "pages/moovs-market/helpers/enumToTrack";
import MoovsDialog from 'components/MoovsDialog';
import { useOperator, useSnackbar } from 'globals/hooks';
import { LOAD_LAGO_PAYMENT_METHOD_QUERY } from 'globals/graphql';
import useOperatorCurrency from 'globals/hooks/useOperatorCurrency';
import { LagoPaymentMethods, PlanInfoSection } from 'pages/settings/SubscriptionsPage/components/SubscriptionPlans/components/UpgradeSubscriptionDialog/components';
import useInstallMarketApp from 'pages/moovs-market/components/MoovsMarketAppInfo/components/ChooseReviewPlatformDialog/hooks/useInstallMarketApp';

import { InfoIcon } from 'design-system/icons';
import { black, granite, grayLight } from 'design-system/colors';

import { MarketApp } from 'pages/moovs-market/components/MoovsMarketAppList/data/appData';

type PassengerAppSubscriptionDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubscribeSuccess: () => void;
  planName: string;
  bgColor: string;
  pricePerMonth: number;
  setupFee: number;
  isFreeUser: boolean;
};

export function PassengerAppSubscriptionDialog(props: PassengerAppSubscriptionDialogProps) {
  const { open, onClose, planName, bgColor, pricePerMonth, setupFee, isFreeUser, onSubscribeSuccess } = props;
  
  // State
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [hideLoadingIndicator, setHideLoadingIndicator] = useState(true);
  const [lagoPaymentMethods, setLagoPaymentMethods] = useState([]);

  // Hooks
  const snackbar = useSnackbar();
  const operator = useOperator();
  const currency = useOperatorCurrency();
  const history = useHistory();
  const { handleInstallMoovsMarketApp, loading: installLoading } = useInstallMarketApp(
    MarketApp.PassengerApp,
    () => {
      snackbar.success('Successfully subscribed to Passenger App!');
      onClose();
      onSubscribeSuccess();
    }
  );

  // Constants
  const displayPrice = currency(pricePerMonth);

  // Queries
  const { loading: paymentMethodLoading } = useQuery(
    LOAD_LAGO_PAYMENT_METHOD_QUERY,
    {
      variables: {
        id: operator?.id,
      },
      fetchPolicy: "cache-and-network",
      skip: !open || !operator?.id,
      onCompleted: (data) => {
        const primaryPaymentMethod = filter(
          data.node.lagoPaymentMethod,
          "isPrimary"
        );
        setLagoPaymentMethods(primaryPaymentMethod);

        if (primaryPaymentMethod.length) {
          setSubmitDisabled(false);
        }
      },
      onError: () => {
        snackbar.error();
      },
    }
  );

  // Handlers
  const handleSubscribe = () => {
    setSubmitDisabled(true);
    setHideLoadingIndicator(false);
    
    handleInstallMoovsMarketApp(installToTrack[MarketApp.PassengerApp], installFailedToTrack[MarketApp.PassengerApp]);
  };

  const handleUpgradePlanClick = () => {
    history.push('/settings/billing/plans');
    onClose();
  };

  return (
    <MoovsDialog
      open={open}
      onClose={onClose}
      onAccept={handleSubscribe}
      acceptDisabled={submitDisabled || installLoading || isFreeUser}
      hideLoadingIndicator={hideLoadingIndicator && !installLoading}
      dialogTitle={`Subscribe to ${planName}`}
      acceptButtonText="Subscribe"
      closeButtonText="Cancel"
      size="sm"
    >
      <Box py={2}>
        <PlanInfoSection
          planName={planName}
          bgColor={bgColor}
          pricePerMonth={pricePerMonth}
        />

        {/* Payment method section */}
        {isFreeUser ? (
          <Box>
            <CardContent
              sx={{
                border: `1px solid ${grayLight}`,
                borderRadius: "4px",
                mt: 3,
                p: 3,
              }}
            >
              <Button
                sx={{ textTransform: 'none', p: 0, minHeight: 0 }}
                onClick={handleUpgradePlanClick}
              >
                <Box display="flex" alignItems="center" p={1} minHeight="40px">
                  <Typography variant="body2" color={black}>
                    This app is for paid users only. Please click here to upgrade your plan.
                  </Typography>
                </Box>
              </Button>
            </CardContent>
          </Box>
        ) : (
          paymentMethodLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                p: 2,
                border: '1px solid transparent'
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <LagoPaymentMethods lagoPaymentMethods={lagoPaymentMethods} />
          )
        )}

        {/* Payment details */}
        <Box mt={3} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
            Initial payment: <span style={{ fontWeight: 'bold' }}>{currency(setupFee).slice(0, -3)} setup fee</span>
          </Typography>
          <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
            Monthly subscription: {displayPrice}/month
          </Typography>
        </Box>

        {/* Info note */}
        <Box display="flex" alignItems="flex-start">
          <Box pt={0.5}>
            <InfoIcon size="sm" color={granite} />
          </Box>
          <Typography ml={1} variant="body2" color={granite}>
            Monthly billing starts when the app becomes available on Google Play and Apple App Store.
          </Typography>
        </Box>
      </Box>
    </MoovsDialog>
  );
};

export default PassengerAppSubscriptionDialog; 